import { useMemo } from "react";
import { useAuth } from "@/context/AuthContext";

/**
 * Central RBAC helper for the JEA frontend.
 *
 * The rules mirror `/app/backend/middleware/rbac.py`. The backend is always
 * the source of truth — this hook only decides what to render, never what to
 * allow. If a user finds a way to trigger a mutating API despite the UI
 * hiding it, the backend still returns 403.
 *
 * Roles:
 *   - owner       : the account holder (no `parent_account_id`)
 *   - admin       : team admin — full access to owner's data
 *   - attorney    : read + reports + queries (cannot mutate debtors)
 *   - collector   : queries + mutate debtors (no billing / no team)
 *   - read_only   : view only — no mutations, no queries
 */
export function useRolePermissions() {
  const { user } = useAuth();

  return useMemo(() => {
    const isTeamMember = !!user?.parent_account_id;
    const role = isTeamMember ? (user?.role || "read_only") : "owner";

    const isOwner     = !isTeamMember;
    const isAdmin     = role === "admin";
    const isAttorney  = role === "attorney";
    const isCollector = role === "collector";
    const isReadOnly  = role === "read_only";

    // High-level capabilities — mirror backend rbac.py exactly.
    const canManageTeam    = isOwner || isAdmin;
    const canManageBilling = isOwner || isAdmin;
    const canMutateDebtors = isOwner || isAdmin || isCollector;
    const canRunQueries    = isOwner || isAdmin || isAttorney || isCollector;
    const canWriteNotes    = canMutateDebtors;
    const canEditSettings  = isOwner || isAdmin; // some settings sections (e.g. tags) are admin-scoped

    // Sidebar / route access. A route is "hidden" if:
    //   (1) it doesn't appear in the sidebar for this role, AND
    //   (2) any attempt to navigate directly by URL should redirect + toast.
    const hiddenRoutes = new Set();
    if (!canManageBilling) hiddenRoutes.add("/billing");
    // /debtors/new is not a sidebar item but is a mutating flow. Block direct
    // URL access for non-mutators (backend enforces 403 anyway).
    if (!canMutateDebtors) hiddenRoutes.add("/debtors/new");
    // /run-query is a page dedicated to launching broker queries. Read-only
    // has no reason to be there and the CTAs would all be blocked anyway.
    if (!canRunQueries) hiddenRoutes.add("/run-query");

    // Whether a given pathname is blocked for the current user.
    const isRouteBlocked = (pathname) => {
      if (!pathname) return false;
      for (const r of hiddenRoutes) {
        if (pathname === r || pathname.startsWith(r + "/")) return true;
      }
      return false;
    };

    return {
      user, role, isTeamMember,
      isOwner, isAdmin, isAttorney, isCollector, isReadOnly,
      canManageTeam, canManageBilling, canMutateDebtors, canRunQueries,
      canWriteNotes, canEditSettings,
      hiddenRoutes, isRouteBlocked,
    };
  }, [user]);
}

export default useRolePermissions;
